import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Upload, FileVideo, Loader2, CheckCircle, ArrowLeft, Sparkles } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { apiService } from '../services/api';

const InterestForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    age: '',
    contactNumber: '',
    discordName: '',
    danceExperience: '',
    singingExperience: '',
    personalStory: '',
    videoUrl: '',
    videoFileName: '',
    externalVideoUrl: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setVideoFile(null); setVideoUrl(''); handleInputChange('videoUrl',''); handleInputChange('videoFileName','');

    const validTypes = ['video/mp4','video/quicktime','video/x-msvideo','video/x-ms-wmv','audio/mp3','audio/mpeg'];
    const maxSize = 25 * 1024 * 1024; // 25MB to ensure email attachment limit
    if (!validTypes.includes(file.type)) { toast({ title: 'Invalid file type', description: 'Upload MP4/MOV/AVI/WMV/MP3', variant: 'destructive' }); return; }
    if (file.size > maxSize) { toast({ title: 'File too large', description: 'Max 25MB for email attachment.', variant: 'destructive' }); return; }

    setVideoFile(file); setVideoUploading(true);
    try {
      toast({ title: 'Uploading video...' });
      const result = await apiService.uploadVideo(file);
      setVideoUrl(result.url);
      handleInputChange('videoUrl', result.url);
      handleInputChange('videoFileName', result.filename);
      toast({ title: 'Video uploaded successfully!' });
    } catch (e) {
      console.error(e); toast({ title: 'Upload failed', description: e.message, variant: 'destructive' });
      setVideoFile(null); setVideoUrl(''); handleInputChange('videoUrl',''); handleInputChange('videoFileName','');
    } finally { setVideoUploading(false); }
  };

  const validateForm = () => {
    const required = ['name','email','age','contactNumber','discordName','danceExperience','singingExperience','personalStory'];
    const missing = required.filter(f => !formData[f]?.toString().trim());
    if (missing.length) { toast({ title: 'Missing Information', description: 'Please fill out all required fields.', variant: 'destructive' }); return false; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; if (!emailRegex.test(formData.email)) { toast({ title: 'Invalid Email', variant: 'destructive' }); return false; }
    const age = parseInt(formData.age); if (isNaN(age) || age < 18) { toast({ title: 'Invalid Age', description: 'You must be at least 18 years old.', variant: 'destructive' }); return false; }
    if (!formData.videoUrl && !formData.externalVideoUrl.trim()) { toast({ title: 'Video/Audio Required', description: 'Upload a file or provide a URL.', variant: 'destructive' }); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      toast({ title: 'Submitting application...' });
      const submissionData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        age: parseInt(formData.age),
        contactNumber: formData.contactNumber.trim(),
        discordName: formData.discordName.trim(),
        danceExperience: formData.danceExperience,
        singingExperience: formData.singingExperience,
        personalStory: formData.personalStory.trim(),
        videoUrl: formData.videoUrl || formData.externalVideoUrl.trim(),
        videoFileName: formData.videoFileName || undefined,
        externalVideoUrl: formData.externalVideoUrl.trim() || undefined,
      };
      const result = await apiService.submitInterestForm(submissionData);
      if (result.success) {
        toast({ title: 'Application Submitted!', description: result.message });
        setFormData({ name:'',email:'',age:'',contactNumber:'',discordName:'',danceExperience:'',singingExperience:'',personalStory:'',videoUrl:'',videoFileName:'',externalVideoUrl:'' });
        setVideoFile(null); setVideoUrl('');
        setTimeout(() => navigate('/Prism'), 1500);
      }
    } catch (error) {
      console.error(error); toast({ title: 'Submission Failed', description: error.message, variant: 'destructive' });
    } finally { setIsSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-midnight-ink via-midnight-ink/95 to-midnight-ink py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Button variant="ghost" onClick={() => navigate('/Prism')} className="mb-6 text-white hover:text-white"><ArrowLeft size={16} className="mr-2" /> Back to Home</Button>
          <Badge className="mb-4 bg-prism-violet/10 text-prism-violet border-prism-violet/20"><Sparkles size={16} className="mr-2" /> Artist Application</Badge>
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4">Join Studio Prism Entertainment</h1>
          <p className="text-lg text-white max-w-2xl mx-auto">Take the first step towards your idol career. Fill out the application below and show us your talent.</p>
        </div>

        <Card className="bg-midnight-ink/50 border-prism-violet/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl text-white">Application Form</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white border-b border-prism-violet/20 pb-2">Personal Information</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2"><Label htmlFor="name" className="text-white">Full Name *</Label><Input id="name" value={formData.name} onChange={(e)=>handleInputChange('name', e.target.value)} placeholder="Enter your full name" className="bg-white/90 border-prism-violet/30 text-gray-800 placeholder:text-gray-500 focus:border-prism-violet focus:bg-white" disabled={isSubmitting} /></div>
                  <div className="space-y-2"><Label htmlFor="email" className="text-white">Email Address *</Label><Input id="email" type="email" value={formData.email} onChange={(e)=>handleInputChange('email', e.target.value)} placeholder="your.email@example.com" className="bg-white/90 border-prism-violet/30 text-gray-800 placeholder:text-gray-500 focus:border-prism-violet focus:bg-white" disabled={isSubmitting} /></div>
                </div>
                <div className="grid md:grid-cols-1 gap-6">
                  <div className="space-y-2"><Label htmlFor="age" className="text-white">Age *</Label><Input id="age" type="number" min="18" value={formData.age} onChange={(e)=>handleInputChange('age', e.target.value)} placeholder="Your age (18+)" className="bg-white/90 border-prism-violet/30 text-gray-800 placeholder:text-gray-500 focus:border-prism-violet focus:bg-white" disabled={isSubmitting} /></div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2"><Label htmlFor="contact" className="text-white">Contact Number *</Label><Input id="contact" type="tel" value={formData.contactNumber} onChange={(e)=>handleInputChange('contactNumber', e.target.value)} placeholder="+82 10-1234-5678" className="bg-white/90 border-prism-violet/30 text-gray-800 placeholder:text-gray-500 focus:border-prism-violet focus:bg-white" disabled={isSubmitting} /></div>
                  <div className="space-y-2"><Label htmlFor="discord" className="text-white">Discord Name *</Label><Input id="discord" value={formData.discordName} onChange={(e)=>handleInputChange('discordName', e.target.value)} placeholder="username#1234 or @username" className="bg-white/90 border-prism-violet/30 text-gray-800 placeholder:text-gray-500 focus:border-prism-violet focus:bg-white" disabled={isSubmitting} /></div>
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white border-b border-prism-violet/20 pb-2">Experience & Skills</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2"><Label className="text-white">Dance Experience *</Label><Select onValueChange={(v)=>handleInputChange('danceExperience', v)} value={formData.danceExperience} disabled={isSubmitting}><SelectTrigger className="bg-white/90 border-prism-violet/30 text-gray-800"><SelectValue placeholder="Select your dance experience level" /></SelectTrigger><SelectContent className="bg-white border-prism-violet/30 text-gray-800"><SelectItem value="beginner">Beginner (0-1 years)</SelectItem><SelectItem value="intermediate">Intermediate (2-4 years)</SelectItem><SelectItem value="advanced">Advanced (5+ years)</SelectItem><SelectItem value="professional">Professional</SelectItem></SelectContent></Select></div>
                  <div className="space-y-2"><Label className="text-white">Singing Experience *</Label><Select onValueChange={(v)=>handleInputChange('singingExperience', v)} value={formData.singingExperience} disabled={isSubmitting}><SelectTrigger className="bg-white/90 border-prism-violet/30 text-gray-800"><SelectValue placeholder="Select your singing experience level" /></SelectTrigger><SelectContent className="bg-white border-prism-violet/30 text-gray-800"><SelectItem value="beginner">Beginner (0-1 years)</SelectItem><SelectItem value="intermediate">Intermediate (2-4 years)</SelectItem><SelectItem value="advanced">Advanced (5+ years)</SelectItem><SelectItem value="professional">Professional</SelectItem></SelectContent></Select></div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-white border-b border-prism-violet/20 pb-2">Performance Video/Audio *</h3>
                <p className="text-white text-sm">Upload a video showcasing your singing and dancing abilities, an audio recording of your singing, or a general pitch video introducing yourself (Max 25MB, MP4/MOV/AVI/WMV/MP3)</p>
                <div className="space-y-4">
                  <div className="space-y-2"><Label className="text-white">Video/Audio URL (YouTube, Drive, etc.) - Alternative to file upload</Label><Input type="url" value={formData.externalVideoUrl} onChange={(e)=>handleInputChange('externalVideoUrl', e.target.value)} placeholder="https://youtube.com/watch?v=... or https://drive.google.com/..." className="bg-white/90 border-prism-violet/30 text-gray-800 placeholder:text-gray-500 focus:border-prism-violet focus:bg-white" disabled={isSubmitting || videoUploading} /><p className="text-xs text-white/80">Provide a direct link to your video or audio instead of uploading a file</p></div>
                  <div className="text-center"><span className="text-white text-sm">- OR -</span></div>
                </div>
                <div className="border-2 border-dashed border-prism-violet/30 rounded-lg p-8">
                  <div className="text-center">
                    {videoUrl ? (
                      <div className="space-y-4">
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
                        <p className="text-white font-medium">File uploaded successfully!</p>
                        <p className="text-white text-sm">{videoFile?.name}</p>
                        <Button type="button" variant="outline" onClick={()=>{ setVideoFile(null); setVideoUrl(''); handleInputChange('videoUrl',''); handleInputChange('videoFileName',''); }} className="border-prism-violet/30 text-prism-violet hover:bg-prism-violet/10" disabled={isSubmitting}>Upload Different File</Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="w-12 h-12 mx-auto">{videoUploading ? (<Loader2 className="w-full h-full text-prism-violet animate-spin" />) : (<FileVideo className="w-full h-full text-prism-violet" />)}</div>
                        <div>
                          <label htmlFor="video-upload" className="cursor-pointer">
                            <Button type="button" disabled={videoUploading || isSubmitting} className="bg-prism-violet hover:bg-prism-violet/90 text-white" asChild>
                              <span>{videoUploading ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Uploading...</>) : (<><Upload size={16} className="mr-2" /> Choose Video/Audio File</>)}</span>
                            </Button>
                          </label>
                          <input id="video-upload" type="file" accept="video/*,audio/mp3,audio/mpeg" onChange={handleVideoUpload} className="hidden" disabled={videoUploading || isSubmitting} />
                        </div>
                        <p className="text-white text-xs">Drag and drop or click to upload video or audio files</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-white border-b border-prism-violet/20 pb-2">Tell Us About Yourself *</h3>
                <div className="space-y-2"><Label htmlFor="story" className="text-white">Share your story, background, dreams, and goals. Why do you want to become an idol?</Label><Textarea id="story" value={formData.personalStory} onChange={(e)=>handleInputChange('personalStory', e.target.value)} placeholder="Tell us about your background, where you're from, your dreams and aspirations, and why you want to become an idol. What makes you unique?" rows={6} className="bg-white/90 border-prism-violet/30 text-gray-800 placeholder:text-gray-500 focus:border-prism-violet focus:bg-white resize-none" disabled={isSubmitting} /></div>
              </div>

              <div className="pt-6">
                <Button type="submit" disabled={isSubmitting || videoUploading} size="lg" className="w-full bg-gradient-to-r from-prism-violet to-prism-violet/80 hover:from-prism-violet/90 hover:to-prism-violet text-white border-0 shadow-lg hover:shadow-prism-violet/25 disabled:opacity-50 disabled:cursor-not-allowed">{isSubmitting ? (<><Loader2 size={18} className="mr-2 animate-spin" /> Submitting Application...</>) : ('Submit Application')}</Button>
                <p className="text-white text-sm text-center mt-4">By submitting this application, you agree to our terms and conditions.</p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InterestForm;